import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";
import createFileList from "create-file-list/dist/create-file-list.mjs";

const MAX_ZOOM = 4;
const SLIDER_MAX = 1000;
const WHEEL_SENSITIVITY = 0.0015;
const OUTPUT_TYPE = "image/webp";
const OUTPUT_QUALITY = 0.92;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const extensionFor = (type) => (type === OUTPUT_TYPE ? ".webp" : ".png");

const toBlob = (canvas, type) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob?.type === type ? blob : null), type, OUTPUT_QUALITY);
  });

export default class extends Controller {
  static targets = [
    "input", "cached", "remove", "zone", "image", "placeholder", "overlayLabel", "filename",
    "notice", "modal"
  ];

  static values = {
    maxSize: { type: Number, default: 2000 },
    labels: Object
  };

  connect() {
    this.pointers = new Map();
    this.objectUrls = new Set();
    this.source = null;
    this.crop = null;

    this.adoptDialog(this.modalTarget);
    this.syncZone();
  }

  disconnect() {
    this.modal.dispose();
    this.dialog.remove();
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }

  adoptDialog(dialog) {
    this.dialog = dialog;
    this.stage = dialog.querySelector(".image-crop__stage");
    this.canvas = dialog.querySelector(".image-crop__image");
    this.slider = dialog.querySelector(".image-crop__zoom input");

    document.body.appendChild(dialog);
    this.modal = new Modal(dialog);

    dialog.addEventListener("shown.bs.modal", this.layout);
    dialog.querySelector(".js-crop-reset").addEventListener("click", this.reset);
    dialog.querySelector(".js-crop-apply").addEventListener("click", this.confirm);
    this.slider.addEventListener("input", this.zoomChanged);
    this.stage.addEventListener("pointerdown", this.pointerDown);
    this.stage.addEventListener("pointermove", this.pointerMove);
    this.stage.addEventListener("pointerup", this.pointerUp);
    this.stage.addEventListener("pointercancel", this.pointerUp);
    this.stage.addEventListener("dblclick", this.reset);
    this.stage.addEventListener("wheel", this.wheel, { passive: false });
  }


  choose(event) {
    event?.preventDefault();
    this.inputTarget.click();
  }

  zoneClicked(event) {
    event.preventDefault();
    this.source ? this.open() : this.choose();
  }

  keydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;

    this.zoneClicked(event);
  }

  async fileChosen(event) {
    await this.adopt(event.target.files[0]);
  }

  dragOver(event) {
    event.preventDefault();
    this.zoneTarget.classList.add("is-dragging");
  }

  dragLeave() {
    this.zoneTarget.classList.remove("is-dragging");
  }

  async dropped(event) {
    event.preventDefault();
    this.zoneTarget.classList.remove("is-dragging");
    await this.adopt(event.dataTransfer.files[0]);
  }

  clear(event) {
    event.preventDefault();
    event.stopPropagation();

    this.inputTarget.value = null;
    this.cachedTarget.value = "";
    this.removeTarget.checked = true;
    this.source = null;
    this.crop = null;
    this.stagedFile = null;
    this.notify(null);
    this.imageTarget.removeAttribute("src");
    this.filenameTarget.textContent = this.labelsValue.empty;
    this.syncZone();
  }


  async adopt(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) return this.reject();

    const source = await this.decode(file);
    if (!source) return this.keepRaw(file);

    this.originalFile = file;
    this.source = source;
    this.crop = centredSquare(source);

    await this.render();
  }

  async decode(file) {
    const image = new Image();
    image.src = this.trackedUrl(file);

    try {
      await image.decode();
      return image;
    } catch {
      return null;
    }
  }

  reject() {
    this.inputTarget.files = createFileList(this.stagedFile ?? []);
    this.notify(this.labelsValue.rejected);
  }

  keepRaw(file) {
    this.source = null;
    this.crop = null;
    this.stageFile(file);
    this.notify(null);
    this.announce(file.name, this.trackedUrl(file));
  }

  stageFile(file) {
    this.stagedFile = file;
    this.inputTarget.files = createFileList(file);
  }

  notify(message) {
    this.noticeTarget.textContent = message ?? "";
    this.noticeTarget.classList.toggle("d-none", !message);
  }


  async render() {
    const blob = await this.cropToBlob();
    const name = this.originalFile.name.replace(/\.[^.]+$/, "") + extensionFor(blob.type);

    this.stageFile(new File([blob], name, { type: blob.type }));
    this.removeTarget.checked = false;
    this.notify(null);
    this.announce(this.originalFile.name, this.trackedUrl(blob));
  }

  async cropToBlob() {
    const { x, y, size } = this.crop;
    const side = Math.min(Math.round(size), this.maxSizeValue);
    const canvas = document.createElement("canvas");

    canvas.width = side;
    canvas.height = side;

    const context = canvas.getContext("2d");
    context.imageSmoothingQuality = "high";
    context.drawImage(this.source, x, y, size, size, 0, 0, side, side);

    return await toBlob(canvas, OUTPUT_TYPE) || await toBlob(canvas, "image/png");
  }

  announce(filename, url) {
    this.filenameTarget.textContent = filename;
    this.imageTarget.src = url;
    this.syncZone();
    this.pop();
  }

  pop() {
    this.zoneTarget.classList.remove("is-fresh");
    void this.zoneTarget.offsetWidth;
    this.zoneTarget.classList.add("is-fresh");
  }

  syncZone() {
    const filled = this.imageTarget.hasAttribute("src");

    this.imageTarget.classList.toggle("d-none", !filled);
    this.placeholderTarget.classList.toggle("d-none", filled);
    this.element.classList.toggle("is-filled", filled);
    this.overlayLabelTarget.textContent =
      this.source ? this.labelsValue.crop : this.labelsValue.replace;
  }


  open() {
    this.canvas.src = this.source.src;
    this.modal.show();
  }

  layout = () => {
    this.frame = this.stage.clientWidth;
    this.minScale = Math.max(
      this.frame / this.source.naturalWidth,
      this.frame / this.source.naturalHeight
    );

    this.scale = this.frame / this.crop.size;
    this.offsetX = -this.crop.x * this.scale;
    this.offsetY = -this.crop.y * this.scale;

    this.paint();
  };

  paint() {
    this.scale = clamp(this.scale, this.minScale, this.minScale * MAX_ZOOM);
    this.offsetX = clamp(this.offsetX, this.frame - this.source.naturalWidth * this.scale, 0);
    this.offsetY = clamp(this.offsetY, this.frame - this.source.naturalHeight * this.scale, 0);

    this.canvas.style.transform =
      `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`;
    this.slider.value = Math.round(
      (Math.log(this.scale / this.minScale) / Math.log(MAX_ZOOM)) * SLIDER_MAX
    );
  }

  zoomTo(scale, anchor) {
    const next = clamp(scale, this.minScale, this.minScale * MAX_ZOOM);
    const ratio = next / this.scale;

    this.offsetX = anchor.x - (anchor.x - this.offsetX) * ratio;
    this.offsetY = anchor.y - (anchor.y - this.offsetY) * ratio;
    this.scale = next;

    this.paint();
  }

  zoomChanged = () => {
    const centre = { x: this.frame / 2, y: this.frame / 2 };

    this.zoomTo(this.minScale * MAX_ZOOM ** (this.slider.value / SLIDER_MAX), centre);
  };

  wheel = (event) => {
    event.preventDefault();
    this.zoomTo(this.scale * Math.exp(-event.deltaY * WHEEL_SENSITIVITY), this.stagePoint(event));
  };

  reset = (event) => {
    event?.preventDefault();
    this.scale = this.minScale;
    this.offsetX = (this.frame - this.source.naturalWidth * this.minScale) / 2;
    this.offsetY = (this.frame - this.source.naturalHeight * this.minScale) / 2;
    this.paint();
  };

  confirm = async () => {
    this.crop = {
      x: -this.offsetX / this.scale,
      y: -this.offsetY / this.scale,
      size: this.frame / this.scale
    };

    this.modal.hide();
    await this.render();
  };


  pointerDown = (event) => {
    event.preventDefault();
    this.stage.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, this.stagePoint(event));
    this.pinchSpan = this.span();
    this.stage.classList.add("is-grabbing");
  };

  pointerMove = (event) => {
    if (!this.pointers.has(event.pointerId)) return;

    const previous = this.pointers.get(event.pointerId);
    const current = this.stagePoint(event);
    this.pointers.set(event.pointerId, current);

    if (this.pointers.size > 1) return this.pinch();

    this.offsetX += current.x - previous.x;
    this.offsetY += current.y - previous.y;
    this.paint();
  };

  pointerUp = (event) => {
    this.pointers.delete(event.pointerId);
    this.pinchSpan = this.span();

    if (this.pointers.size === 0) this.stage.classList.remove("is-grabbing");
  };

  pinch() {
    const [a, b] = [...this.pointers.values()];
    const span = distance(a, b);

    if (this.pinchSpan) this.zoomTo(this.scale * (span / this.pinchSpan), midpoint(a, b));
    this.pinchSpan = span;
  }

  span() {
    if (this.pointers.size < 2) return null;

    const [a, b] = [...this.pointers.values()];

    return distance(a, b);
  }

  stagePoint(event) {
    const bounds = this.stage.getBoundingClientRect();

    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  }

  trackedUrl(blob) {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);

    return url;
  }
}

const centredSquare = ({ naturalWidth, naturalHeight }) => {
  const size = Math.min(naturalWidth, naturalHeight);

  return { x: (naturalWidth - size) / 2, y: (naturalHeight - size) / 2, size };
};
